// Noyau — ARBRE PONDÉRÉ (lot F4). Bloc outil de la feuille, comme le tableau : mode « build » (l'élève crée les
// branches à partir de la racine) ou « given » (arbre fourni, à compléter).
//
//   model.tree = { mode, levels: [["A", "B"], ["D", "D̄"]], p: { "A": "0,3", "D|A": "0,05", "D|B": "0,02" }, paths: true }
//   p : probabilité d'un événement sachant le chemin qui y mène (« D|A », « E|A∩D ») ; la dernière d'une fratrie
//       se déduit (somme = 1). paths : l'élève écrit aussi la probabilité de chaque chemin complet (produit).
//
// L'arbre de l'élève : { root: { uid, children: [node] } }, node = { uid, label: case, p: case, path?: case, children }
// (case = { uid, text, kind: 'word' | 'expr', locked? }). judgeTree → { cells: { uid → verdict }, notes, done }.

import { num, Q, symbols } from './ast.js';
import { parse } from './syntax.js';
import { evaluate } from './equiv.js';
import { atomsOf, renderTemplate, ratText, decText } from './pedagogy.js';

const toQ = (v) => (v && typeof v === 'object' && typeof v.p === 'number' ? num(v.p, v.q) : null);
const ONE = num(1);
const eqQ = (a, b) => !!a && !!b && Q.eq(a, b);
const isEmpty = (c) => !c || !String(c.text ?? '').trim();
const norm = (s) =>
  String(s ?? '')
    .normalize('NFC')
    .trim();
const keyOf = (e, path) => (path.length ? `${e}|${path.join('∩')}` : e);

export function treeModel(engine, exercise) {
  const spec = exercise.model?.tree;
  if (!spec) throw new Error('cet exercice n’a pas d’arbre (model.tree)');
  const table = engine.table;
  const levels = spec.levels.map((l) => l.map(String));
  if (!levels.length || levels.some((l) => l.length < 2))
    throw new Error('arbre : chaque niveau a au moins deux événements');
  const M = {
    kind: 'tree',
    spec,
    engine,
    table,
    mode: spec.mode === 'build' ? 'build' : 'given',
    levels,
    paths: !!spec.paths,
    P: engine.tables?.kinds?.tree || {},
    numbers: new Set(),
    frac: false,
    parens: true,
  };
  const given = {};
  for (const [k, t] of Object.entries(spec.p || {})) {
    const a = parse(String(t), table);
    const v = toQ(evaluate(a, {}, table));
    if (!v) throw new Error(`arbre : « ${t} » n’est pas une probabilité`);
    given[k.replace(/\s+/g, '')] = v;
    // les DONNÉES de l'énoncé, telles quelles, au clavier : 0,3 plutôt que 30 et 100 ; une fraction si l'écriture décimale est infinie
    M.numbers.add(decText(v) ?? ratText(v));
    if (decText(v) == null) {
      const at = atomsOf(a, table);
      at.nums.forEach((x) => M.numbers.add(x));
      M.frac = true;
    }
  }
  M.numbers.add('1');
  // probabilité de e sachant le chemin : donnée, ou complément de la fratrie (une seule inconnue par fratrie)
  M.prob = (e, path) => {
    const d = path.length,
      sib = levels[d];
    const k = keyOf(e, path);
    if (given[k]) return given[k];
    const known = sib.filter((x) => x !== e).map((x) => given[keyOf(x, path)]);
    if (known.some((x) => !x)) return null;
    return known.reduce((acc, x) => Q.sub(acc, x), ONE);
  };
  // contrôle : toutes les branches ont une probabilité calculable
  const walk = (path) => {
    if (path.length === levels.length) return;
    for (const e of levels[path.length]) {
      if (!M.prob(e, path)) throw new Error(`arbre : il manque P(${keyOf(e, path)})`);
      walk([...path, e]);
    }
  };
  walk([]);
  M.words = [...new Set(levels.flat())];
  return M;
}

export function givenTree(M) {
  const show = new Set((M.spec.given?.p || []).map((k) => k.replace(/\s+/g, '')));
  const mk = (path) =>
    M.levels[path.length]?.map((e) => {
      const p2 = [...path, e];
      const pv = M.prob(e, path);
      return {
        label: { text: e, kind: 'word', locked: true },
        p: show.has(keyOf(e, path))
          ? { text: ratText(pv), kind: 'expr', locked: true }
          : { text: '', kind: '' },
        path: M.paths && p2.length === M.levels.length ? { text: '', kind: '' } : null,
        children: mk(p2) || [],
      };
    }) || [];
  return { root: { children: mk([]) }, locked: true };
}

function cellValue(M, c) {
  if (isEmpty(c) || c.kind === 'word') return { err: 'Cette case attend un nombre.' };
  let a;
  try {
    a = parse(String(c.text), M.table);
  } catch {
    return { err: 'Je n’arrive pas à lire la case.' };
  }
  if ([...symbols(a)].some((s) => M.table.syntax.constants[s] == null))
    return { err: 'Une probabilité est un nombre.' };
  const v = toQ(evaluate(a, {}, M.table));
  if (!v) return { err: 'Ce calcul n’a pas de valeur.' };
  return { a, v };
}
const written = (M, a) => {
  const ev = (n) => M.table.nodes[n.t]?.eval;
  const n0 = ev(a) === 'neg' ? a.k[0] : a;
  return n0.t === 'Num' || (ev(n0) === 'div' && n0.k.every((k) => k.t === 'Num' && k.q === 1));
};
function judgeP(M, c, exp, alts, facts) {
  if (isEmpty(c)) return null;
  const r = cellValue(M, c);
  if (r.err) return { status: 'wrong', title: 'Cette case n’est pas juste', message: r.err };
  if (eqQ(r.v, exp))
    return written(M, r.a)
      ? { status: 'ok', value: r.v }
      : {
          status: 'unfinished',
          title: M.P.unfinished?.title || 'Juste : finis le calcul',
          message: renderTemplate(M.P.unfinished?.why || '', facts),
        };
  for (const [det, vals] of alts) {
    if ((Array.isArray(vals) ? vals : [vals]).some((w) => eqQ(w, r.v) && !eqQ(w, exp))) {
      const t = M.P.traps?.[det];
      if (t)
        return {
          status: 'trap',
          trap: det,
          title: renderTemplate(t.title, facts),
          message: renderTemplate(t.why, facts),
        };
    }
  }
  return {
    status: 'wrong',
    title: renderTemplate(M.P.wrong?.title || 'Cette case n’est pas juste', facts),
    message: renderTemplate(M.P.wrong?.value || '', { ...facts, answer: ratText(r.v) }),
  };
}

export function judgeTree(M, tree) {
  const out = { cells: {}, notes: [], done: false };
  const put = (c, v) => {
    if (c?.uid && v && !c.locked) out.cells[c.uid] = v;
  };
  let complete = true,
    missing = 0;
  const visit = (node, path, pathProbs) => {
    const d = path.length;
    if (d === M.levels.length) {
      // feuille : probabilité du chemin
      if (M.paths) {
        const exp = pathProbs.reduce((a, b) => Q.mul(a, b), ONE);
        const sum = pathProbs.reduce((a, b) => Q.add(a, b), num(0));
        const facts = { path: path.join(' ∩ ') };
        if (isEmpty(node.path)) complete = false;
        else {
          const v = judgeP(M, node.path, exp, [['sumPath', sum]], facts);
          put(node.path, v);
          if (v?.status !== 'ok') complete = false;
        }
      }
      if (node.children?.length) {
        for (const ch of node.children) markExtra(ch);
      }
      return;
    }
    const seen = new Set();
    for (const ch of node.children || []) {
      const lab = norm(ch.label?.text);
      let ok = false;
      if (isEmpty(ch.label)) {
        complete = false;
        markPending(ch, 'Écris d’abord l’événement de cette branche.');
        continue;
      }
      if (!M.levels[d].includes(lab)) {
        const lv = M.levels.findIndex((l) => l.includes(lab));
        const t = lv >= 0 ? M.P.traps?.wrongLevel : null;
        put(
          ch.label,
          t
            ? {
                status: 'trap',
                trap: 'wrongLevel',
                title: renderTemplate(t.title, { event: lab, level: lv + 1, here: d + 1 }),
                message: renderTemplate(t.why, { event: lab, level: lv + 1, here: d + 1 }),
              }
            : { status: 'wrong', title: 'Cet événement ne va pas ici', message: '' },
        );
      } else if (seen.has(lab))
        put(ch.label, {
          status: 'wrong',
          title: 'Branche en double',
          message: `Il y a déjà une branche ${lab} à ce nœud.`,
        });
      else {
        put(ch.label, { status: 'ok' });
        ok = true;
        seen.add(lab);
      }
      if (!ok) {
        complete = false;
        markPending(ch, 'Corrige d’abord l’événement de cette branche.', true);
        continue;
      }
      const exp = M.prob(lab, path);
      const facts = { event: lab, parent: path.at(-1) || '', path: [...path, lab].join(' ∩ ') };
      const alts = [['complement', Q.sub(ONE, exp)]];
      if (d >= 1) {
        // probabilité non conditionnelle : celle de lab sous un AUTRE parent, ou P(lab) totale
        const other = [];
        const tot = [];
        const enumPaths = (p, k) => {
          if (k === d) {
            tot.push(p);
            return;
          }
          for (const e of M.levels[k]) enumPaths([...p, e], k + 1);
        };
        enumPaths([], 0);
        let total = num(0);
        for (const p of tot) {
          const w = p.reduce((acc, e, i) => Q.mul(acc, M.prob(e, p.slice(0, i))), ONE);
          total = Q.add(total, Q.mul(w, M.prob(lab, p)));
          if (p.join() !== path.join()) other.push(M.prob(lab, p));
        }
        alts.push(['unconditional', [...other, total]]);
        alts.push(['joint', pathProbs.reduce((a, b) => Q.mul(a, b), exp)]);
      }
      if (isEmpty(ch.p)) complete = false;
      else {
        const v = judgeP(M, ch.p, exp, alts, facts);
        put(ch.p, v);
        if (v?.status !== 'ok') complete = false;
      }
      visit(ch, [...path, lab], [...pathProbs, exp]);
    }
    const miss = M.levels[d].filter((e) => !seen.has(e)).length;
    if (miss) {
      complete = false;
      missing += miss;
    }
  };
  const markPending = (node, msg, deep = false) => {
    if (!isEmpty(node.p)) put(node.p, { status: 'pending', message: msg });
    if (node.path && !isEmpty(node.path)) put(node.path, { status: 'pending', message: msg });
    for (const ch of node.children || []) {
      if (!isEmpty(ch.label)) put(ch.label, { status: 'pending', message: msg });
      markPending(ch, msg, deep);
    }
  };
  const markExtra = (node) => {
    put(node.label, {
      status: 'wrong',
      title: 'Branche en trop',
      message: `L’arbre a ${M.levels.length} niveau${M.levels.length > 1 ? 'x' : ''}.`,
    });
    for (const ch of node.children || []) markExtra(ch);
  };
  visit(tree.root || { children: [] }, [], []);
  if (missing)
    out.notes.push({
      key: 'missingBranches',
      message: renderTemplate(
        M.P.notes?.missingBranches || `Il manque ${missing} branche${missing > 1 ? 's' : ''}.`,
        { n: missing },
      ),
    });
  const bad = Object.values(out.cells).some((v) =>
    ['wrong', 'trap', 'unfinished'].includes(v.status),
  );
  out.done = complete && !bad;
  return out;
}

// Touches d'une case de l'arbre : les événements (tous niveaux, ordre fixe) ou un nombre.
// Les nombres : les données de l'énoncé ET ceux que l'élève a déjà écrits dans l'arbre (0,99 = 1 − 0,01, pour un chemin).
export function treeKeys(M, which, tree = null) {
  if (which === 'label') return { role: 'label', words: M.words, choices: [], palette: null };
  const nums = new Set(M.numbers);
  const walk = (n) => {
    for (const c of [n.p, n.path]) {
      if (isEmpty(c) || c.kind === 'word') continue;
      const r = cellValue(M, c);
      if (r.v && written(M, r.a)) nums.add(decText(r.v) ?? ratText(r.v));
    }
    (n.children || []).forEach(walk);
  };
  if (tree?.root) walk(tree.root);
  const val = (t) =>
    parseFloat(
      t
        .replace(',', '.')
        .replace('−', '-')
        .replace(/^(-?\d+)\/(\d+)$/, (m, a, b) => String(a / b)),
    );
  return {
    role: which,
    words: [],
    choices: [],
    palette: {
      ids: [],
      numbers: [...nums].sort((a, b) => val(a) - val(b)),
      ops: ['+', '−', '×', '÷'],
      frac: M.frac,
      parens: true,
      chunks: [],
    },
  };
}
export function treeHint(M, which, depth = 1) {
  const key = which === 'p' ? (depth > 1 ? 'p2' : 'p1') : which;
  const tpl = M.P.hints?.[key] || M.P.hint;
  return tpl ? renderTemplate(tpl, {}) : null;
}
